import type { ApiResponse } from '#shared/types/api'

// Response from API - matches database structure (snake_case)
interface Period {
  id: string
  name: string
  slug: string
  description: string | null
  sort_order: number | null
  is_active: boolean | null
  created_at: string | null
  updated_at: string | null
}

// Input payload for database - uses snake_case
interface CreatePeriodPayload {
  name: string
  description?: string
  sort_order?: number
  is_active?: boolean
}

export function usePeriods() {
  /**
   * Get all periods
   */
  const getPeriods = async (activeOnly = false): Promise<ApiResponse<Period[]>> => {
    const query = activeOnly ? '?active_only=true' : ''

    return await $api(`/api/periods${query}`, {
      method: 'GET',
    })
  }

  /**
   * Get period by ID
   */
  const getPeriodById = async (id: string): Promise<ApiResponse<Period>> => {
    return await $api(`/api/periods/${id}`, {
      method: 'GET',
    })
  }

  /**
   * Create a new period
   */
  const createPeriod = async (payload: CreatePeriodPayload): Promise<ApiResponse<Period>> => {
    return await $api('/api/periods', {
      method: 'POST',
      body: payload,
    })
  }

  /**
   * Update a period
   */
  const updatePeriod = async (
    id: string,
    payload: Partial<CreatePeriodPayload>,
  ): Promise<ApiResponse<Period>> => {
    return await $api(`/api/periods/${id}`, {
      method: 'PATCH',
      body: payload,
    })
  }

  /**
   * Delete a period
   */
  const deletePeriod = async (id: string): Promise<ApiResponse<void>> => {
    return await $api(`/api/periods/${id}`, {
      method: 'DELETE',
    })
  }

  return {
    getPeriods,
    getPeriodById,
    createPeriod,
    updatePeriod,
    deletePeriod,
  }
}
